import { useState, useEffect } from 'react';
import { X, Loader, Radio, Plus, Trash2 } from 'lucide-react';
import { supabase, Playlist, PlaylistTrack } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type CreateZoneModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onZoneCreated?: () => void;
  onStartDrawing?: () => void;
  drawnPolygon?: [number, number][] | null;
};

export function CreateZoneModal({
  isOpen,
  onClose,
  onZoneCreated,
  onStartDrawing,
  drawnPolygon
}: CreateZoneModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<'draw' | 'info'>('draw');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [playlistId, setPlaylistId] = useState<string>('');
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [newTrackUrl, setNewTrackUrl] = useState('');
  const [newTrackTitle, setNewTrackTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);


  const addTrack = async () => {
    if (!user || !newTrackUrl.trim() || !newTrackTitle.trim()) return;

    try {
      // Create playlist automatically if not exists (using zone name)
      let currentPlaylistId = playlistId;
      if (!currentPlaylistId) {
        const playlistName = name.trim() || 'Untitled Zone';
        const { data: newPlaylist, error: playlistError } = await supabase
          .from('playlists')
          .insert({
            user_id: user.id,
            name: playlistName,
            description: '',
            is_public: true
          })
          .select()
          .single();

        if (playlistError) throw playlistError;
        currentPlaylistId = newPlaylist.id;
        setPlaylistId(currentPlaylistId);
      }

      const { data, error } = await supabase
        .from('playlist_tracks')
        .insert({
          playlist_id: currentPlaylistId,
          youtube_url: newTrackUrl,
          title: newTrackTitle,
          position: tracks.length
        })
        .select()
        .single();

      if (error) throw error;

      setTracks([...tracks, data]);
      setNewTrackUrl('');
      setNewTrackTitle('');
    } catch (err) {
      console.error('Error adding track:', err);
    }
  };

  const deleteTrack = async (trackId: string) => {
    try {
      const { error } = await supabase
        .from('playlist_tracks')
        .delete()
        .eq('id', trackId);

      if (error) throw error;
      setTracks(tracks.filter(t => t.id !== trackId));
    } catch (err) {
      console.error('Error deleting track:', err);
    }
  };

  const calculateCenter = (coords: [number, number][]): { lat: number; lng: number } => {
    const sumLat = coords.reduce((sum, [lng, lat]) => sum + lat, 0);
    const sumLng = coords.reduce((sum, [lng, lat]) => sum + lng, 0);
    return {
      lat: sumLat / coords.length,
      lng: sumLng / coords.length
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !drawnPolygon || drawnPolygon.length < 3) {
      setError('Please draw a zone on the map first');
      return;
    }

    if (!name.trim()) {
      setError('Please enter a zone name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const center = calculateCenter(drawnPolygon);

      const { error: insertError } = await supabase
        .from('sound_zones')
        .insert({
          user_id: user.id,
          name,
          description,
          latitude: center.lat,
          longitude: center.lng,
          radius: 100,
          shape_type: 'polygon',
          coordinates: drawnPolygon,
          playlist_id: playlistId || null,
          is_active: true
        });

      if (insertError) throw insertError;

      onZoneCreated?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create zone');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('draw');
    setName('');
    setDescription('');
    setPlaylistId('');
    setTracks([]);
    setError(null);
    setNewTrackUrl('');
    setNewTrackTitle('');
    onClose();
  };

  if (!isOpen) return null;

  const isPolygonComplete = drawnPolygon && drawnPolygon.length >= 3 &&
    drawnPolygon[0][0] === drawnPolygon[drawnPolygon.length - 1][0] &&
    drawnPolygon[0][1] === drawnPolygon[drawnPolygon.length - 1][1];
  const isDrawingActive = step === 'draw' && !isPolygonComplete;

  return (
    <div className={`fixed ${isDrawingActive ? 'pointer-events-none' : 'inset-0 bg-black bg-opacity-50'} flex items-center justify-center z-50 p-4`}>
      <div className={`bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto ${isDrawingActive ? 'fixed bottom-4 right-4 max-w-md' : ''}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Radio className="w-6 h-6 text-[#00c7b7]" />
              <h2 className="text-2xl font-bold">Create Sound Zone</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {step === 'draw' && (
            <div className="space-y-4">
              <div className="bg-teal-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Step 1: Draw Your Zone</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Click on the map to create points. Connect back to the first point to complete the polygon.
                </p>
                {!drawnPolygon || drawnPolygon.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      console.log('Start drawing clicked');
                      onStartDrawing?.();
                    }}
                    className="w-full px-4 py-2 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] font-medium"
                  >
                    Start Draw Sound Zone on Map
                  </button>
                ) : !isPolygonComplete ? (
                  <div>
                    <div className="flex items-center gap-2 text-[#00c7b7] mb-3">
                      <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Drawing in progress ({drawnPolygon.length} points)</span>
                    </div>
                    <p className="text-sm text-gray-300 bg-gray-900 p-3 rounded">
                      Click near the first point to close the polygon
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 text-green-600 mb-3">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Polygon complete ({drawnPolygon.length} points)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep('info')}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      Continue to Zone Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'info' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Zone Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder="e.g., Downtown Music District"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                  placeholder="Describe the vibe or theme of this zone..."
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Add Music ({tracks.length} {tracks.length === 1 ? 'song' : 'songs'})
                </label>

                <div className="mb-3 space-y-2">
                  <input
                    type="text"
                    value={newTrackTitle}
                    onChange={(e) => setNewTrackTitle(e.target.value)}
                    placeholder="Song title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    value={newTrackUrl}
                    onChange={(e) => setNewTrackUrl(e.target.value)}
                    placeholder="YouTube URL"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={addTrack}
                    disabled={!newTrackTitle.trim() || !newTrackUrl.trim()}
                    className="w-full px-3 py-2 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    + Add More Sound
                  </button>
                </div>

                {tracks.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tracks.map((track, index) => (
                      <div key={track.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{index + 1}. {track.title}</p>
                          <p className="text-xs text-gray-500 truncate">{track.youtube_url}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteTrack(track.id)}
                          className="ml-2 p-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('draw')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Radio className="w-4 h-4" />
                      Create Zone
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
